import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  AuthMethod,
  AuthService,
  InitiateAuthResponse,
  MFAMethod,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { authnClient } from '@frontend/auth';
import { getWeaveToken } from '@frontend/auth-helpers';
import { useTranslation, Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { EmailIcon, Heading, MessageIcon, NakedButton, Button, Text, TextLink } from '@frontend/design-system';
import { baseFetch } from '../services/fetch-wrapper';
import { MFAInput } from './mfa-input.component';
import { SignInError, SignInErrorTypeKey } from './sign-in-error.component';

type Props = {
  mfaMethods: MFAMethod[];
  onHandleMFAViewToggle: (value: boolean) => void;
  handleSignIn: () => void;
  challenge: string;
  showDisplayNames: boolean;
  onToggleDisplayNames: (value: boolean) => void;
  registerWebauthn?: boolean;
  rememberMe?: boolean;
  onIsCheckingPasswordToggle: (value: boolean) => void;
  username?: string;
};

export const MFAForm = ({
  mfaMethods,
  onHandleMFAViewToggle,
  handleSignIn,
  challenge,
  showDisplayNames,
  onToggleDisplayNames,
  registerWebauthn,
  rememberMe,
  onIsCheckingPasswordToggle,
  username,
}: Props) => {
  const { t } = useTranslation('SignIn');
  const [code, setCode] = useState<string>();
  const [disableAction, setDisableAction] = useState(true);
  const [selectedContact, setSelectedContact] = useState<MFAMethod>({});
  const [resendTimer, setResendTimer] = useState(15);
  const [canResend, setCanResend] = useState(false);
  const [errorCode, setErrorCode] = useState<SignInErrorTypeKey | undefined>();
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [input, setInput] = useState('');
  const mfaInputRef = useRef<HTMLInputElement>(null);

  const handleMFAError = (error: SignInErrorTypeKey) => {
    setIsError(true);
    setErrorCode(error);
  };

  const handleInputValue = (value: string) => {
    setInput(value);
  };

  const handleCodeVerification = (mfaCode?: string) => {
    setLoading(true);
    setLoadingMessage(t('Verifying code...'));
    authnClient
      .signIn({ username, code: mfaCode ?? code, challenge, registerWebauthn, rememberMe })
      .then((res) => {
        setLoading(false);
        if (res.token) {
          handleSignIn();
        } else {
          throw new Error('No token returned');
        }
      })
      .catch((err) => {
        const token = getWeaveToken();
        if (token) {
          // if we got an error, and we have a token, we successfully logged in but webauthn failed
          console.error('Issue setting up Webauthn', err);
          handleMFAError('webauthnSetupError');
          handleSignIn();
          return;
        }
        setLoading(false);
        handleMFAError('invalidMFACode');
        console.error('Failed to verify contact', err);
        setInput(''); // clear input on error
        mfaInputRef.current?.focus(); // refocus input
      });
  };

  const handleNewDisplayName = (method: MFAMethod) => {
    handleInputValue('');
    onToggleDisplayNames(false);
    setResendTimer(15);
    setCanResend(false);
    setLoading(true);
    setLoadingMessage(t('Sending new code...'));
    AuthService.InitiateAuth(baseFetch<InitiateAuthResponse>(), {
      method: {
        id: method.id,
        displayName: method.displayName,
        type: method.type,
      },
      challenge,
    })
      .then(() => {
        setLoading(false);
        setSelectedContact(method);
      })
      .catch((err) => {
        setLoading(false);
        console.error('Failed to send a new code', err);
        handleMFAError('codeSendFailure');
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: err,
        });
      });
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timerId);
    } else {
      setCanResend(true);
    }
    return undefined;
  }, [resendTimer]);

  useEffect(() => {
    if (!mfaMethods) return;
    const selectedMethod = mfaMethods.find((method) => method.codeSent) || mfaMethods[0];
    setSelectedContact(selectedMethod);
  }, [mfaMethods]);

  return (
    <>
      {!showDisplayNames ? (
        <section
          css={css`
            min-width: 280px;
            max-width: 400px;
            align-self: center;
          `}
        >
          <Heading level={2}>
            {selectedContact?.type === AuthMethod.EMAIL ? t('We Emailed You a Code') : t('We Texted You a Code')}
          </Heading>
          <Text
            css={css`
              margin-top: ${theme.spacing(1)};
            `}
            color='light'
          >
            {t(`Enter the verification code sent to ${selectedContact?.displayName} `)}
          </Text>

          <div
            css={css`
              max-width: 350px;
              min-width: 280px;
              display: flex;
              flex-direction: column;
              margin-top: ${theme.spacing(4)};
            `}
          >
            <MFAInput
              ref={mfaInputRef}
              input={input}
              onInputChange={handleInputValue}
              setCode={setCode}
              setDisableAction={setDisableAction}
              onSubmit={handleCodeVerification}
            />
            <Button
              variant='primary'
              css={css`
                margin-top: ${theme.spacing(3)};
              `}
              onClick={() => handleCodeVerification()}
              disabled={disableAction || loading}
              id='mfa-login-button'
            >
              {loading ? loadingMessage : t('Log In')}
            </Button>
            <div
              css={css`
                margin-top: ${theme.spacing(4)};
                display: block;
                text-align: center;
                gap: ${theme.spacing(1)};
              `}
            >
              {t('Didn’t get your code? ')}
              {!canResend ? (
                <Text>{t(`Send a new code in ${resendTimer} seconds`)}</Text>
              ) : (
                <TextLink
                  css={css`
                    cursor: pointer;
                  `}
                  onClick={() => handleNewDisplayName(selectedContact)}
                  id='mfa-send-new-code-button'
                >
                  {t('Send a new code')}
                </TextLink>
              )}
            </div>
            <div
              css={css`
                margin-top: ${theme.spacing(4)};
                display: block;
                text-align: center;
                gap: ${theme.spacing(1)};
              `}
            >
              <Trans t={t}>
                Learn how to manage MFA in the{' '}
                <TextLink
                  href='https://www.weavehelp.com/weavehelp/article/turn-on-multi-factor-authentication'
                  target='_blank'
                >
                  Help Center
                </TextLink>
                .
              </Trans>
            </div>
            {mfaMethods && mfaMethods.length > 1 && (
              <Text
                css={css`
                  margin: ${theme.spacing(2, 1)};
                `}
              >
                {t('Having trouble? ')}
                <TextLink onClick={() => onToggleDisplayNames(true)}>{t('Change verification method')}</TextLink>
              </Text>
            )}
            {isError && <SignInError errorCode={errorCode} />}
          </div>

          <TextLink
            css={css`
              display: flex;
              margin-top: ${theme.spacing(5)};
              cursor: pointer;
              align-items: center;
              gap: ${theme.spacing(1)};
            `}
            onClick={() => {
              onHandleMFAViewToggle(false);
              onIsCheckingPasswordToggle(false);
            }}
            id='change-user-button'
          >
            <Icon name='back-small' />
            {t('Change user')}
          </TextLink>
        </section>
      ) : (
        <section
          css={css`
            width: 400px;
            align-self: center;
          `}
        >
          <Heading
            css={css`
              margin: ${theme.spacing(0, 6)};
            `}
            level={3}
          >
            {t('Select a Verification Method')}
          </Heading>
          {mfaMethods &&
            mfaMethods.map((method) => (
              // TODO: let button handle click, not the section
              <section
                css={css`
                  margin-top: ${theme.spacing(4)};
                `}
                onClick={() => handleNewDisplayName(method)}
                key={method.id}
                id='mfa-send-code-button'
              >
                <NakedButton
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border: 1px solid ${theme.colors.neutral20};
                    border-radius: ${theme.borderRadius.medium};
                    padding: ${theme.spacing(2)};
                    width: 100%;
                    gap: ${theme.spacing(2)};
                    margin-top: ${theme.spacing(2)};
                    font-size: ${theme.font.size.medium};
                    font-weight: ${theme.font.weight.bold};
                    color: ${theme.colors.neutral70};
                    &:hover {
                      background-color: ${theme.colors.neutral10};
                    }
                  `}
                >
                  {method.type === 'EMAIL' ? <EmailIcon /> : <MessageIcon />}
                  {t(`Send Code to ${method.displayName}`)}
                </NakedButton>
              </section>
            ))}
        </section>
      )}
    </>
  );
};
